import { useRef, useState } from "react";
import { ChevronsLeftRight, ImageIcon } from "lucide-react";
import { BrowserFrame } from "@/components/site/BrowserFrame";

const Pane = ({ kind, project }) => {
  const isBefore = kind === "before";
  const src = isBefore ? project.beforeImage : project.afterImage;
  if (src) {
    return (
      <img
        src={src}
        alt={`${isBefore ? "Outdated website" : "Redesigned website"} concept for ${project.name}`}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
    );
  }
  return (
    <div className={`hatch absolute inset-0 flex items-end sm:items-center ${isBefore ? "justify-start bg-[#16171A]" : "justify-end bg-[#0E1320]"}`}>
      {!isBefore && (
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(60% 70% at 80% 30%, ${project.accent}30, transparent 70%)` }}
        />
      )}
      <div className={`relative px-4 pb-5 sm:px-10 sm:pb-0 ${isBefore ? "text-left" : "text-right"}`}>
        <ImageIcon className={`h-6 w-6 text-white/40 ${isBefore ? "" : "ml-auto"}`} aria-hidden="true" />
        <p className="mt-3 font-mono text-xs uppercase tracking-wide text-white/80 sm:tracking-[0.18em]">
          {isBefore ? "Before" : "After"}
          <span className="hidden sm:inline"> — </span>
          <br className="sm:hidden" />
          image placeholder
        </p>
        <p className="mt-1.5 hidden text-sm text-[#A1A1AA] sm:block">
          {isBefore ? "Outdated site screenshot" : "Redesign screenshot"}
        </p>
      </div>
    </div>
  );
};

export const BeforeAfter = ({ project }) => {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const areaRef = useRef(null);
  const clamp = (n) => Math.min(100, Math.max(0, n));

  const update = (clientX) => {
    const rect = areaRef.current?.getBoundingClientRect();
    if (rect) setPos(clamp(((clientX - rect.left) / rect.width) * 100));
  };

  const onPointerDown = (e) => {
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (e.pointerType === "mouse") update(e.clientX);
  };

  const onKeyDown = (e) => {
    const step = e.shiftKey ? 10 : 5;
    const moves = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step };
    if (e.key in moves) {
      e.preventDefault();
      setPos((p) => clamp(p + moves[e.key]));
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      setPos(e.key === "Home" ? 0 : 100);
    }
  };

  const value = Math.round(pos);
  return (
    <BrowserFrame url={`${project.short.toLowerCase().replace(/[^a-z]+/g, "")}.example`}>
      <div
        ref={areaRef}
        onPointerDown={onPointerDown}
        onPointerMove={(e) => dragging && update(e.clientX)}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        className="relative aspect-[16/10] cursor-ew-resize touch-pan-y select-none overflow-hidden"
        data-testid={`before-after-${project.id}`}
      >
        <Pane kind="after" project={project} />
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <Pane kind="before" project={project} />
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 w-px -translate-x-1/2 bg-white/80 shadow-[0_0_14px_rgba(255,255,255,0.6)]"
          style={{ left: `${pos}%` }}
        />
        <div
          role="slider"
          tabIndex={0}
          aria-label={`Compare the outdated site and the redesign for ${project.name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={value}
          aria-valuetext={`${value}% before, ${100 - value}% after`}
          onKeyDown={onKeyDown}
          data-testid={`before-after-handle-${project.id}`}
          className="absolute top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/30 bg-[#0A0B0D]/80 text-white shadow-lg backdrop-blur-md transition-[box-shadow] duration-200 hover:shadow-[0_0_0_6px_rgba(47,107,255,0.25)]"
          style={{ left: `${pos}%` }}
        >
          <ChevronsLeftRight className="h-4 w-4" aria-hidden="true" />
        </div>
        <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 font-mono text-xs uppercase tracking-wider text-white/90">
          Before
        </span>
        <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-[#2F6BFF] px-2.5 py-1 font-mono text-xs uppercase tracking-wider text-white">
          After
        </span>
      </div>
    </BrowserFrame>
  );
};
